import { Autocomplete, Divider, Grid, TextField, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useFieldContext } from "@/components/Forms/form.context";
import { useAutocompleteSuggestions } from "@/hooks/useAddressAutoComplete";
import type { AddressPrefix, AddressValues, DeliveryFormType } from "../types";

// ─── Internal: Address Autocomplete Input ───────────────────────────────────

function AddressSearchInput({
  form,
  identifier,
  disabled,
}: {
  form: DeliveryFormType;
  identifier: AddressPrefix;
  disabled: boolean;
  onAddressSelected?: (address: AddressValues) => void;
}) {
  const { t } = useTranslation();
  const field = useFieldContext<string>();
  const addressValue = field.state.value ?? "";

  const { suggestions, resetSession, isLoading } = useAutocompleteSuggestions(addressValue);

  const predictions = useMemo(
    () =>
      suggestions.filter((s) => s.placePrediction).map(({ placePrediction }) => placePrediction),
    [suggestions]
  );

  const handleSelect = useCallback(
    (prediction: google.maps.places.PlacePrediction | string) => {
      if (typeof prediction === "string") return;
      const address = prediction?.text.text;
      const place = prediction.toPlace();
      place
        .fetchFields({ fields: ["location", "addressComponents"] })
        .then(() => {
          resetSession();
          const components = place.addressComponents;
          if (!components) return;

          const city = components.find((c) => c.types.includes("locality"))?.longText;
          const postalCode = components.find((c) => c.types.includes("postal_code"))?.longText;
          const country = components.find((c) => c.types.includes("country"))?.shortText;

          form.setFieldValue(`${identifier}.longitude`, place.location?.lng());
          form.setFieldValue(`${identifier}.latitude`, place.location?.lat());
          form.setFieldValue(`${identifier}.place_id`, place.id);
          form.setFieldValue(`${identifier}.postalCode`, postalCode ?? "");
          form.setFieldValue(`${identifier}.city`, city ?? "");
          form.setFieldValue(`${identifier}.country`, country ?? "");
          field.handleChange(address ?? "");
          if (onAddressSelected) {
            onAddressSelected({
              address: address ?? "",
              city: city ?? "",
              postalCode: postalCode ?? "",
              country: country ?? "",
              latitude: place.location?.lat() ?? 0,
              longitude: place.location?.lng() ?? 0,
              place_id: place.id ?? "",
            });
          }
        })
        .catch(() => {});
    },
    [resetSession, form, identifier, field, onAddressSelected]
  );

  return (
    <Autocomplete
      getOptionLabel={(option) => (typeof option === "string" ? option : option?.text?.text)}
      disabled={disabled}
      freeSolo
      loading={isLoading}
      disableClearable
      filterOptions={(x) => x}
      options={predictions}
      onChange={(_event, selected) => handleSelect(selected)}
      value={addressValue}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{ textTransform: "capitalize" }}
          variant="outlined"
          label={t("common.form.input.search-an-address")}
          InputLabelProps={addressValue ? { shrink: true } : {}}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          InputProps={{ ...params.InputProps, type: "search" }}
          placeholder="123 rue de Paris, 75001 Paris"
        />
      )}
    />
  );
}

// ─── Public: Address Section ────────────────────────────────────────────────

interface Props {
  form: DeliveryFormType;
  title: string;
  identifier: AddressPrefix;
  disabled?: boolean;
  onAddressSelected?: (address: AddressValues) => void;
}

export function AddressSection({
  form,
  title,
  identifier,
  disabled = false,
  onAddressSelected,
}: Props) {
  const { t } = useTranslation();

  return (
    <Grid container direction="row" spacing={2} paddingBottom={2}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h5" textTransform="capitalize">
          {title}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ bgcolor: "#fe5d8d", marginBottom: 3 }} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <form.AppField name={`${identifier}.address`}>
          {() => (
            <AddressSearchInput
              form={form}
              identifier={identifier}
              disabled={disabled}
              onAddressSelected={onAddressSelected}
            />
          )}
        </form.AppField>
      </Grid>

      <Grid size={{ xs: 4 }}>
        <form.AppField name={`${identifier}.city`}>
          {(f) => <f.TextField label={t("common.form.input.city")} disabled />}
        </form.AppField>
      </Grid>

      <Grid size={{ xs: 4 }}>
        <form.AppField name={`${identifier}.postalCode`}>
          {(f) => <f.TextField label={t("common.form.input.zip-code")} disabled />}
        </form.AppField>
      </Grid>

      <Grid size={{ xs: 4 }}>
        <form.AppField name={`${identifier}.country`}>
          {(f) => <f.TextField label={t("common.form.input.country")} disabled />}
        </form.AppField>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <form.AppField name={`${identifier}.addressPrecision`}>
          {(f) => (
            <f.TextField label={t("common.form.input.address-accuracy")} disabled={disabled} />
          )}
        </form.AppField>
      </Grid>
    </Grid>
  );
}
